import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, fetchClient, type components } from "@/api/client";
import { body } from "@/api/call";
import { previewSourceKey, type PreviewSource } from "@/lib/dataset-preview";

export type PreviewSample = components["schemas"]["DraftMember"] | components["schemas"]["MembershipOut"];
export type SampleCursor = { offset?: number; after?: string };

/** Queries are mounted only for the current catalog page and start when its media enters the viewport. */
export function useVisiblePreview() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "80px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

export function useDatasetSamples(slug: string, name: string, source: PreviewSource | undefined,
  limit: number, cursor: SampleCursor = {}, enabled = true, seed?: number) {
  return useQuery(datasetSamplesQueryOptions(slug, name, source, limit, cursor, enabled, seed));
}

export function datasetSamplesQueryOptions(slug: string, name: string, source: PreviewSource | undefined,
  limit: number, cursor: SampleCursor = {}, enabled = true, seed?: number) {
  return {
    queryKey: ["dataset-catalog-samples", slug, name, source && previewSourceKey(source), limit, cursor, seed ?? null],
    enabled: enabled && !!source,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<{ samples: PreviewSample[]; total: number | null; next: SampleCursor | null }> => {
      if (!source) throw new Error("Select a draft revision or a published version.");
      if (source.kind === "draft") {
        const result = await body(fetchClient.GET("/api/projects/{slug}/datasets/{name}/drafts/{number}/revisions/{revision}/samples", {
          signal, params: { path: { slug, name, number: source.number, revision: source.revision }, query: { limit, offset: cursor.offset ?? 0, seed } },
        }));
        const offset = result.offset + result.members.length;
        return { samples: result.members, total: result.total, next: offset < result.total ? { offset } : null };
      }
      const result = await body(fetchClient.GET("/api/projects/{slug}/datasets/{name}/versions/{ref}/files", {
        signal, params: { path: { slug, name, ref: `v${source.number}` }, query: { limit, after: cursor.after ?? "", seed } },
      }));
      return { samples: result.samples, total: null, next: result.next ? { after: result.next } : null };
    },
  };
}

export function usePreviewVersion(slug: string, name: string, source: PreviewSource | undefined, enabled = true) {
  return api.useQuery("get", "/api/projects/{slug}/datasets/{name}/versions/{ref}", {
    params: { path: { slug, name, ref: `v${source?.number ?? 1}` } },
  }, { enabled: enabled && source?.kind === "version", retry: false, staleTime: 60_000 });
}

/** Fetch through the generated client so permission/error behavior is identical to other API reads. */
export function useSampleImage(slug: string, name: string, source: PreviewSource, sha: string, enabled: boolean, size: "thumbnail" | "display" = "display") {
  const query = useQuery({
    queryKey: ["dataset-catalog-image", slug, name, previewSourceKey(source), sha, size],
    enabled, retry: false, staleTime: Infinity, gcTime: 5 * 60_000,  // exact sha + exact version: the bytes never change; returning to a page reuses them
    queryFn: async ({ signal }) => {
      if (source.kind === "draft") return body(fetchClient.GET(
        "/api/projects/{slug}/datasets/{name}/drafts/{number}/revisions/{revision}/samples/{sample_sha256}/preview", {
          signal, parseAs: "blob", params: { path: { slug, name, number: source.number, revision: source.revision, sample_sha256: sha }, query: { size } },
        }));
      return body(fetchClient.GET("/api/projects/{slug}/datasets/{name}/versions/{ref}/samples/{sample_sha256}/preview", {
        signal, parseAs: "blob", params: { path: { slug, name, ref: `v${source.number}`, sample_sha256: sha }, query: { size } },
      }));
    },
  });
  const [image, setImage] = useState<{ blob: Blob; url: string }>();
  useEffect(() => {
    if (!query.data) return;
    const url = URL.createObjectURL(query.data);
    setImage({ blob: query.data, url });
    return () => URL.revokeObjectURL(url);
  }, [query.data]);
  return { ...query, url: image?.blob === query.data ? image?.url : undefined };
}
